import React, { type ReactElement, type ReactNode } from 'react';
import { ArrowSquareOutIcon } from '@stashinvest/react-assets';
import { BanjoString } from '@stashinvest/react-banjo';
import { useBankAccount } from 'stash/selectors';
import { DisabledFeaturesList } from 'stash/routes/BankRoute';
import type { ReduxDebitAccount } from 'stash/types/redux/entities/bank';

export const ExternalIcon = (): ReactElement => (
	<ArrowSquareOutIcon size="2XS" className="align-middle" />
);

export type SettingsPage = {
	title: ReactNode;
	pathname: string;
	activeMatches?: string[];
	showInArchive?: boolean;
	showInContentMoat?: boolean;
	archived?: boolean;
	disallowed?: string;
	icon?: () => ReactElement;
	onClick?: () => void;
};

export type GetSettingsPagesArgs = {
	archive?: string | boolean;
	isContentMoat?: boolean;
	disabledFeatures?: string[];
	hasAdvisorySignature?: boolean;
	isPaywalled?: boolean;
	isMarketInsightsEnabled?: boolean;
	isNotificationCenterEnabled?: boolean;
	hasUnreadNotifications?: boolean;
};

export type PageGroupArgs = {
	archive: string;
};

export type PageGroup = (args: PageGroupArgs) => SettingsPage[];

export const BankPages: SettingsPage[] = [
	{
		title: 'Manage card',
		pathname: `/debit/cardManagement`,
		showInArchive: false,
		showInContentMoat: false,
	},
	{
		title: 'Temporary Virtual Card',
		pathname: `/debit/settings/virtualCard`,
		showInArchive: false,
		showInContentMoat: false,
		disallowed: DisabledFeaturesList.VirtualCard,
	},
	{
		title: <BanjoString stringId="management/function/autoAddCash" />,
		pathname: `/debit/recurringTransfers`,
		showInArchive: false,
		showInContentMoat: false,
		disallowed: DisabledFeaturesList.RecurringTransfer,
	},
	{
		title: 'ATM Locator',
		showInArchive: false,
		showInContentMoat: false,
		pathname: `https://www.stash.com/atm-locator`,
		icon: ExternalIcon,
		disallowed: DisabledFeaturesList.AtmLocator,
	},
	{
		title: 'Account & Routing numbers',
		showInArchive: false,
		pathname: `/debit/accountInformation`,
		disallowed: DisabledFeaturesList.AccountInfo,
	},
];

export const contentMoatPage: SettingsPage = {
	title: 'Investment and financial advice',
	pathname: `/subscriber-advice`,
	activeMatches: [],
	showInArchive: true,
	showInContentMoat: true,
};

export const getSettingsPages = ({
	archive,
	isContentMoat,
	disabledFeatures,
	hasAdvisorySignature,
	isPaywalled,
	isMarketInsightsEnabled,
	isNotificationCenterEnabled,
	hasUnreadNotifications,
}: GetSettingsPagesArgs): SettingsPage[] => {
	const pages: SettingsPage[] = [
		{
			title: 'Your profile',
			pathname: `/account-settings`,
			activeMatches: [],
			showInArchive: false,
			showInContentMoat: false,
		},
		...(isNotificationCenterEnabled
			? [
					{
						title: (
							<span className="inline-flex items-center justify-end gap-content2XS">
								Notifications
								{hasUnreadNotifications ? (
									<span
										role="img"
										aria-label="Unread notifications"
										className="inline-block h-2 w-2 rounded-full bg-primary"
									/>
								) : null}
							</span>
						),
						pathname: `/notifications`,
						activeMatches: ['/notification-center'],
						showInArchive: false,
						showInContentMoat: false,
					},
			  ]
			: []),
		{
			title: 'Financial profile',
			pathname: `/investor-profile`,
			activeMatches: [],
			showInArchive: false,
			showInContentMoat: false,
		},
		...(isMarketInsightsEnabled
			? [
					{
						title: 'Market insights report',
						pathname: `/market-insights-report`,
						activeMatches: [],
						showInArchive: false,
						showInContentMoat: true,
					},
			  ]
			: []),
		{
			title: 'Refer friends to earn cash',
			pathname: `${archive}/referrals`,
			activeMatches: [],
			showInArchive: false,
			showInContentMoat: false,
		},
		{
			title: 'Enter promo code',
			pathname: `/promotion-code`,
			activeMatches: [],
			showInArchive: false,
			showInContentMoat: false,
		},
		{
			title: 'Email, phone & security',
			pathname: `/security`,
			activeMatches: [],
			showInArchive: false,
			showInContentMoat: false,
		},
		{
			title: 'Portfolio activity',
			pathname: `${archive}/activity`,
			activeMatches: [],
			showInArchive: true,
			showInContentMoat: false,
		},
		{
			title: 'Statements & tax documents',
			pathname: `${archive}/documents`,
			activeMatches: [
				'/statements',
				'/trade-confirmations',
				'/tax-documents',
				'/closed-account-documents',
			],
			showInArchive: true,
			showInContentMoat: false,
		},
		{
			title: 'Securities lending',
			pathname: `/investments_lending`,
			activeMatches: [],
			showInArchive: false,
			showInContentMoat: false,
		},
		{
			title: 'Dividend reinvestment program',
			pathname: `/dividend-reinvestment-program`,
			activeMatches: [],
			showInArchive: false,
			showInContentMoat: false,
		},
		{
			title: 'External bank account',
			pathname: `${archive}/manage-bank-accounts`,
			activeMatches: ['/manage-bank-account', '/manage-bank-accounts'],
			showInArchive: false,
			showInContentMoat: false,
		},
		{
			title: 'Manage plan',
			pathname: `${archive}/plan-details`,
			activeMatches: ['/manage-subscription', '/add-payment-method'],
			showInArchive: true,
			showInContentMoat: true,
		},
		...(hasAdvisorySignature && !isPaywalled ? [contentMoatPage] : []),
		{
			title: 'Notification Preferences',
			pathname: '/notification-settings',
			activeMatches: [],
			archived: false,
			showInContentMoat: false,
		},
		{
			title: 'Data sharing',
			pathname: '/data-sharing',
			activeMatches: [],
			archived: false,
			showInContentMoat: true,
		},
		...(disabledFeatures
			? BankPages.filter(
					(page) => !disabledFeatures.includes(page.disallowed as string)
			  )
			: []),
	];

	const filterIfArchive = (p: SettingsPage) => !archive || p.showInArchive;

	if (isContentMoat) {
		return pages.filter((p) => p.showInContentMoat);
	}

	return pages.filter((p) => filterIfArchive(p));
};

export const useBankSettingsPages = ({ archive }: PageGroupArgs): SettingsPage[] => {
	const account = useBankAccount() as ReduxDebitAccount | undefined;
	const filterIfArchive = (p: SettingsPage) => !archive || p.showInArchive;
	const disallowed = (p: SettingsPage) =>
		!(account?.disabledFeatures ?? []).includes(p.disallowed as string);
	return BankPages.filter(disallowed).filter((p) => filterIfArchive(p));
};
